import { useEffect, useState } from "react";
import type { Control, UseFormSetValue } from "react-hook-form";
import { useWatch } from "react-hook-form";
import { PlusIcon } from "@heroicons/react/outline";
import { MdDeleteOutline } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { Input } from "../../../../common/components/Input";
import type { IMedia } from "../../../../model/unityObject";
import ImageCard from "../../../gallery/components/ImageCard";
import { Tooltip } from "../../../../common/components/Tooltip/Tooltip";

// content-element-catch stores element_list as
//   [{ name, description, image_id, sound_id, score, count, speed, size }]
// on the base content object (source of truth for structure + media + numbers).
// The per-language PLD column (list_element_catch_element_data) carries a
// translation-only projection: each element wrapped under `element` with only
// name/description kept and every numeric/media field zeroed
// (pldElementTransforms.js -> "content-element-catches"). content-element-catch
// is EXCLUDED from the backend afterUpdate PLD sync (index.js ~L503-505,
// alongside content-random-quizz), so the frontend write
// (buildUpdatedProjectLanguageData) is the SOLE PLD writer — it must emit that
// same wrapped/zeroed shape.
//
// This bespoke editor (roadmap §2.D pt 12 / Phase 5 — the 5th and richest
// `fields/` component; precedents: the hotspot constrained editors + the four
// Quiz*Field) keeps the full object array in react-hook-form. NodeForm seeds it
// by merging the content object's structure with the active language's PLD
// name/description (matched BY ARRAY POSITION — element items carry no stable
// id), and round-trips it on the content PUT (source language only) + the PLD.
//
// The item shape has ZERO placement keys (verified across all 365 live items),
// so — unlike the other spatial JSON (spawn_*, hotspot_infos, paintline_info,
// §2.D pt 13) — every field is form-editable. name/description are translatable
// (editable in every language); image_id/sound_id/score/count/size/speed are
// structural (source language only). Bounds/labels come from the VR item
// sub-editor prefab (VRCElementCatch, SetFloat sliders): mirrors the VR limits
// verbatim (§8 — VR is the source of truth for every limit).

// VR caps the list at 20 (AddElement: `Count < 20`) and never below 1
// (RemoveElement: `Count > 1`).
const MIN_ELEMENTS = 1;
const MAX_ELEMENTS = 20;

// VR AddElement defaults (VRCElementCatchBodyController.AddElement):
// count/score/size/speed = 1, image_id = Constants.DEFAULT_IMAGE_ID (176),
// sound_id = Constants.DEFAULT_AUDIO_ELEMENT_CATCH_ID (2890). Mirrored so a
// web-added element matches what the VR editor would create.
const DEFAULT_IMAGE_ID = 176;
const DEFAULT_SOUND_ID = 2890;

// Per-numeric-field VR slider bounds + display mode (Appendix C addendum,
// extracted from VRCElementCatch.prefab SetFloat overrides):
//   score → Multiplier   min 1   max 10  Int
//   count → NbQuestions  min 1   max 20  Int
//   size  → RegionHeight min 0.1 max 10  Decimal
//   speed → RegionWidth  min 0.1 max 20  Decimal
type NumericKey = "score" | "count" | "size" | "speed";
const NUMERIC_FIELDS: {
  key: NumericKey;
  min: number;
  max: number;
  isInt: boolean;
  labelKey: string;
}[] = [
  { key: "score", min: 1, max: 10, isInt: true, labelKey: "elementCatchScore" },
  { key: "count", min: 1, max: 20, isInt: true, labelKey: "elementCatchCount" },
  { key: "size", min: 0.1, max: 10, isInt: false, labelKey: "elementCatchSize" },
  { key: "speed", min: 0.1, max: 20, isInt: false, labelKey: "elementCatchSpeed" },
];

export interface ElementCatchItem {
  name: string;
  description: string;
  image_id: number;
  sound_id: number;
  score: number;
  count: number;
  size: number;
  speed: number;
  [key: string]: unknown;
}

const NOMEDIA_FALLBACK: IMedia = {
  createdAt: "",
  id: -1,
  name: "",
  label: "",
  organization: 3,
  s3_url: "https://s3.eu-west-3.amazonaws.com/storage.wixar.io/NOMEDIA.png",
  type: null,
  updatedAt: "",
  filename: "NOMEDIA.png",
  tags: [],
  upload_status: "completed",
} as unknown as IMedia;

interface ElementCatchListFieldProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  label: string;
  /** Full media library, to resolve each element's image_id/sound_id. */
  mediasList: IMedia[];
  /** Opens the shared gallery modal (image mode) targeting the given row. */
  onPickImage: (index: number) => void;
  /** Opens the shared gallery modal (audio mode) targeting the given row. */
  onPickSound: (index: number) => void;
  /** View mode / mid-generation — fully read-only. */
  disabled?: boolean;
  /** Structural edits (add/remove/media/numbers) are source-language only. */
  isSourceLanguage: boolean;
  /** Optional per-name/description character cap. */
  maxLength?: number;
}

// Clamp to the VR slider bounds (round for Int-display fields). Empty / invalid
// input snaps to the field's min so the number never serializes as "" / NaN (a
// nested "" would land raw in the JSON column and break the VR reader). Mirrors
// the VR editor, which cannot represent an empty or out-of-range slider.
const clampNumber = (raw: string, min: number, max: number, isInt: boolean): number => {
  const n = raw === "" ? min : Number(raw);
  if (!Number.isFinite(n)) return min;
  const bounded = Math.min(Math.max(n, min), max);
  return isInt ? Math.round(bounded) : bounded;
};

// A single numeric cell backed by a local string buffer so free-form typing
// (incl. a trailing "." while entering a decimal) is never clobbered by the
// controlled numeric value. The parsed number commits to react-hook-form on
// every keystroke (kept a valid number, never ""), and the value is SNAPPED into
// the VR [min,max] range on blur — mirroring the VR slider, which can't leave
// its track. External value changes (language reseed, add-default) resync the
// buffer.
const NumberCell = ({
  value,
  min,
  max,
  isInt,
  label,
  disabled,
  onCommit,
}: {
  value: number;
  min: number;
  max: number;
  isInt: boolean;
  label: string;
  disabled?: boolean;
  onCommit: (n: number) => void;
}) => {
  const [text, setText] = useState<string>(() => String(value));

  // Resync the buffer when the committed value changes from the outside (not
  // from our own keystroke — those already match Number(text)).
  useEffect(() => {
    if (Number(text) !== value) setText(String(value));
  }, [value]);

  return (
    <Input
      type="number"
      label={label}
      value={text}
      min={min}
      max={max}
      step={isInt ? 1 : "any"}
      disabled={disabled}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        if (raw !== "") {
          const n = Number(raw);
          if (Number.isFinite(n)) onCommit(clampNumber(raw, min, max, isInt));
        }
      }}
      onBlur={() => {
        const clamped = clampNumber(text, min, max, isInt);
        setText(String(clamped));
        onCommit(clamped);
      }}
      className="dynamic-field"
    />
  );
};

const ElementCatchListField = ({
  control,
  setValue,
  label,
  mediasList,
  onPickImage,
  onPickSound,
  disabled = false,
  isSourceLanguage,
  maxLength,
}: ElementCatchListFieldProps) => {
  const { t } = useTranslation();

  const watched = useWatch({ control, name: "element_list" });
  const elements: ElementCatchItem[] = Array.isArray(watched) ? watched : [];

  // Text (name/description) is editable in any language; structure (media,
  // numbers, add/remove) is locked outside the source language.
  const structureLocked = disabled || !isSourceLanguage;

  const write = (next: ElementCatchItem[]) => setValue("element_list", next, { shouldDirty: true });

  const handleTextChange = (index: number, key: "name" | "description", value: string) => {
    write(elements.map((e, i) => (i === index ? { ...e, [key]: value } : e)));
  };

  const handleNumberCommit = (index: number, key: NumericKey, next: number) => {
    write(elements.map((e, i) => (i === index ? { ...e, [key]: next } : e)));
  };

  const handleAdd = () => {
    write([
      ...elements,
      {
        name: "",
        description: "",
        image_id: DEFAULT_IMAGE_ID,
        sound_id: DEFAULT_SOUND_ID,
        score: 1,
        count: 1,
        size: 1,
        speed: 1,
      },
    ]);
  };

  const handleRemove = (index: number) => {
    write(elements.filter((_, i) => i !== index));
  };

  const canAdd = !structureLocked && elements.length < MAX_ELEMENTS;
  const canRemove = !structureLocked && elements.length > MIN_ELEMENTS;
  const atMinElements = elements.length <= MIN_ELEMENTS;
  const atMaxElements = elements.length >= MAX_ELEMENTS;
  const showMinHint = !disabled && isSourceLanguage && atMinElements;
  const showMaxHint = !disabled && isSourceLanguage && atMaxElements;
  const minHint = t("pages.project.editNode.elementCatchMinHint") as string;
  const maxHint = t("pages.project.editNode.elementCatchMaxHint") as string;

  return (
    <div className="w-full mt-4">
      <div className="flex items-center justify-between mb-2 gap-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <Tooltip
          message={!canAdd && !structureLocked ? maxHint : ""}
          className="shrink-0"
          classNameChildren="right-0"
        >
          <button
            type="button"
            onClick={handleAdd}
            disabled={!canAdd}
            className="btn-alternative-outline flex items-center gap-1.5 text-xs px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            <span>{t("pages.project.editNode.elementCatchAddElement")}</span>
          </button>
        </Tooltip>
      </div>

      {!isSourceLanguage && !disabled && (
        <p className="mb-3 text-xs italic text-gray-500">
          {t("pages.project.editNode.elementCatchStructureLockedHint")}
        </p>
      )}

      {/* Reserved hint line (avoids layout shift) explaining the disabled
          add / remove buttons at the min / max. */}
      <p className="min-h-[1.25rem] mb-2 text-xs font-medium text-gray-500 italic">
        {showMinHint ? minHint : showMaxHint ? maxHint : ""}
      </p>

      <div className="space-y-3">
        {elements.map((element, index) => {
          const image =
            mediasList.find((m) => Number(m.id) === Number(element.image_id)) ?? NOMEDIA_FALLBACK;
          const sound =
            mediasList.find((m) => Number(m.id) === Number(element.sound_id)) ?? NOMEDIA_FALLBACK;
          const rowLabel = t("pages.project.editNode.elementCatchItemLabel", {
            n: index + 1,
            defaultValue: `Element ${index + 1}`,
          });

          return (
            <div key={index} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-600">{rowLabel}</span>
                <Tooltip
                  message={!canRemove && !structureLocked ? minHint : ""}
                  className="shrink-0"
                  classNameChildren="right-0"
                >
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    disabled={!canRemove}
                    className="text-red-500 p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label={t("general.delete") as string}
                  >
                    <MdDeleteOutline size={20} />
                  </button>
                </Tooltip>
              </div>

              {/* Translatable text — editable in every language. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                <Input
                  type="text"
                  label={t("pages.project.editNode.elementCatchName") as string}
                  value={element.name ?? ""}
                  onChange={(e) => handleTextChange(index, "name", e.target.value)}
                  disabled={disabled}
                  maxLength={maxLength}
                  className="dynamic-field"
                />
                <Input
                  type="text"
                  label={t("pages.project.editNode.elementCatchDescription") as string}
                  value={element.description ?? ""}
                  onChange={(e) => handleTextChange(index, "description", e.target.value)}
                  disabled={disabled}
                  maxLength={maxLength}
                  className="dynamic-field"
                />
              </div>

              {/* Media pickers — structural (source language only). */}
              <div className="mt-3 flex flex-wrap gap-4">
                <div className="w-40 shrink-0">
                  <span className="block text-xs font-medium text-gray-700 mb-1">
                    {t("pages.project.editNode.elementCatchImage")}
                  </span>
                  <ImageCard
                    media={image}
                    currentMedia={
                      Number(element.image_id) > 0 ? Number(element.image_id) : undefined
                    }
                    isMediaPickerMode={false}
                    showThreeDotsMenu={false}
                    disabled={structureLocked}
                    handleButtonClick={() => onPickImage(index)}
                    handleCardClick={() => {
                      if (!structureLocked) onPickImage(index);
                    }}
                    className="w-40"
                  />
                </div>
                <div className="w-40 shrink-0">
                  <span className="block text-xs font-medium text-gray-700 mb-1">
                    {t("pages.project.editNode.elementCatchSound")}
                  </span>
                  <ImageCard
                    media={sound}
                    currentMedia={
                      Number(element.sound_id) > 0 ? Number(element.sound_id) : undefined
                    }
                    isMediaPickerMode={false}
                    showThreeDotsMenu={false}
                    disabled={structureLocked}
                    handleButtonClick={() => onPickSound(index)}
                    handleCardClick={() => {
                      if (!structureLocked) onPickSound(index);
                    }}
                    className="w-40"
                  />
                </div>
              </div>

              {/* Numbers — structural (source language only), VR slider bounds. */}
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2">
                {NUMERIC_FIELDS.map((spec) => (
                  <NumberCell
                    key={spec.key}
                    label={t(`pages.project.editNode.${spec.labelKey}`) as string}
                    value={
                      typeof element[spec.key] === "number"
                        ? (element[spec.key] as number)
                        : spec.min
                    }
                    min={spec.min}
                    max={spec.max}
                    isInt={spec.isInt}
                    disabled={structureLocked}
                    onCommit={(n) => handleNumberCommit(index, spec.key, n)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ElementCatchListField;
